import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  CircularProgress,
  Alert
} from '@mui/material';
import {
  CheckCircle,
  Warning,
  Error,
  Pending
} from '@mui/icons-material';
import { ComplianceMonitoringService } from '../../services/ComplianceMonitoringService';

interface ComplianceError {
  message: string;
  code?: string;
  details?: Record<string, any>;
}

interface ComplianceCheck {
  ruleId: string;
  checkType: string;
  compliant: boolean;
  details: Record<string, any>;
  severity?: 'low' | 'medium' | 'high';
  error?: string;
}

interface ComplianceResult {
  id: string;
  status: 'pass' | 'fail' | 'warning';
  message: string;
  details?: any;
}

interface ComplianceCheckerProps {
  fiscalSponsorId: string;
  causeId?: string;
  transactionId?: string;
  checkType: 'transaction' | 'periodic' | 'filing' | 'audit';
  data: any;
  onComplete: (results: ComplianceResult[]) => void;
}

export const ComplianceChecker: React.FC<ComplianceCheckerProps> = ({
  fiscalSponsorId,
  causeId,
  transactionId,
  checkType,
  data,
  onComplete
}) => {
  const [checks, setChecks] = useState<ComplianceCheck[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const complianceService = new ComplianceMonitoringService();

  useEffect(() => {
    performChecks();
  }, [fiscalSponsorId, checkType, data]);

  const performChecks = async () => {
    setLoading(true);
    setError(null);
    try {
      const results = await complianceService.monitorCompliance({
        fiscalSponsorId,
        causeId,
        transactionId,
        checkType,
        metadata: {}
      }, data);
      
      setChecks(results);
      onComplete(results.map(result => ({
        id: result.ruleId,
        status: result.compliant ? 'pass' : result.error ? 'fail' : 'warning',
        message: result.error || result.checkType,
        details: result.details
      })));
    } catch (err) {
      const error = err as ComplianceError;
      const errorMessage = error.message || 'An unknown error occurred';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (check: ComplianceCheck) => {
    if (check.error) return <Error color="error" />;
    if (check.compliant) return <CheckCircle color="success" />;
    return <Warning color={check.severity === 'high' ? 'error' : 'warning'} />;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={3}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error">
        Failed to perform compliance checks: {error}
      </Alert>
    );
  }

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Compliance Checks
      </Typography>
      
      <List>
        {checks.map((check) => (
          <ListItem key={check.ruleId}>
            <ListItemIcon>
              {getStatusIcon(check)}
            </ListItemIcon>
            <ListItemText
              primary={check.checkType}
              secondary={
                check.error || 
                Object.entries(check.details)
                  .map(([key, value]) => `${key}: ${value}`)
                  .join(', ')
              }
            />
            {check.severity && (
              <Chip
                label={check.severity.toUpperCase()}
                color={check.severity === 'high' ? 'error' : 'warning'}
                size="small"
              />
            )}
          </ListItem>
        ))}
      </List>
    </Box>
  );
};

export default ComplianceChecker; 